import { ICellRendererParams } from 'ag-grid-community';
import { useQueryClient } from 'react-query';

import { getAgGridTableCellData } from 'core/utils';
import { PartContent } from 'quotes/components';
import { QuotePartColId } from 'quotes/components/QuotePartList';
import { QuoteType, QuoteVersionType } from 'quotes/enums';
import { useQuotePartActions } from 'quotes/hooks';
import { IAttributePartPayload, IQuotePart } from 'quotes/types';

interface IProps extends ICellRendererParams {
  onPartNumberClick: (quotePart: IQuotePart) => void;
  onEditPartLineClick: (quotePart: IQuotePart) => void;
  isRowEditable?: ({ data }) => boolean;
  isRowSelectPartAllowed?: ({ data }) => boolean;
  setDraftVersionChanged?: () => void;
  quoteVersionType?: QuoteVersionType;
  quoteType?: QuoteType;
}

const PartContentCell = ({
  onPartNumberClick,
  onEditPartLineClick,
  isRowEditable = defaultIsRowEditable,
  isRowSelectPartAllowed = defaultIsRowSelectPartAllowed,
  setDraftVersionChanged = null,
  quoteVersionType = QuoteVersionType.DRAFT,
  quoteType = QuoteType.CUSTOMER,
  ...props
}: IProps) => {
  const queryClient = useQueryClient();

  const { upsertQuoteParts } = useQuotePartActions();

  const data = getAgGridTableCellData(props, QuotePartColId.PART_NUMBER);

  if (!data) {
    return props?.value;
  }

  const onAttachParts = async (parts: IAttributePartPayload[]) => {
    const { success, quoteParts } = await upsertQuoteParts(
      {
        triggerRecordsSaved: true,
        quoteParts: parts.map((part) => ({
          ...part,
          parentEntityPartId: data?.id,
          quoteVersionType: quoteVersionType,
          quoteId: data.quoteId,
          adjustQuantity: false,
          updateLines: true,
        })),
      },
      { onSuccess: setDraftVersionChanged }
    );

    if (!success) {
      return;
    }

    props.api?.setGridOption('loading', true);
    queryClient.invalidateQueries('customerQuoteParts');
    queryClient.invalidateQueries('vendorQuoteParts');

    // A vendor-quote part save can flip trade_agreement_status Sent -> Updated Post Sent
    // server-side with no signal in the response, so refresh the quote the VQ surfaces read.
    if (QuoteType.VENDOR === quoteType) {
      queryClient.invalidateQueries('quote');
    }

    return { newRows: quoteParts };
  };

  return (
    <PartContent
      quotePart={data}
      onPartNumberClick={onPartNumberClick ? () => onPartNumberClick(data) : null}
      onEditPartLineClick={onEditPartLineClick ? () => onEditPartLineClick(data) : null}
      isEditable={isRowEditable({ data })}
      isDeleted={Boolean(data.deleted && !data.omitted)}
      isSelectPartAllowed={isRowSelectPartAllowed({ data })}
      onAttachParts={onAttachParts}
      quoteType={quoteType}
    />
  );
};

export default PartContentCell;

const defaultIsRowEditable = () => false;
const defaultIsRowSelectPartAllowed = () => false;
